import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Loader2, Plus, Trash2, RefreshCw, Settings2, Layers, GitBranch, Database } from 'lucide-react';

const SUB_TABS = [
  { id: 'systems', label: 'Systems', icon: Layers },
  { id: 'subsystems', label: 'Subsystems', icon: GitBranch },
  { id: 'stagegates', label: 'Stage Gates', icon: Settings2 },
];

export default function PCCMConfiguration({ projectId }) {
  const { isDark } = useTheme();
  const [subTab, setSubTab] = useState('systems');
  const [systems, setSystems] = useState([]);
  const [subsystems, setSubsystems] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sysRes, subRes, cfgRes] = await Promise.all([
        api.get(`/v1/projects/${projectId}/pccm/systems`),
        api.get(`/v1/projects/${projectId}/pccm/subsystems`),
        api.get(`/v1/projects/${projectId}/pccm/stage-gate-configs`),
      ]);
      setSystems(sysRes.data || []);
      setSubsystems(subRes.data || []);
      setConfigs(cfgRes.data || []);
    } catch { toast.error('Failed to load configuration'); }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const seedAll = async () => {
    setSeeding(true);
    try {
      await api.post(`/v1/projects/${projectId}/pccm/seed-all`);
      toast.success('Seeded systems, subsystems, stage gate config, and sample entries');
      fetchAll();
    } catch { toast.error('Failed to seed data'); }
    setSeeding(false);
  };

  const deleteSystem = async (code) => {
    try {
      await api.delete(`/v1/projects/${projectId}/pccm/systems/${code}`);
      toast.success('Deleted');
      fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>;

  return (
    <div className="space-y-4" data-testid="pccm-configuration">
      {/* Sub-tabs */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          {SUB_TABS.map(st => {
            const Icon = st.icon;
            return (
              <button key={st.id} onClick={() => setSubTab(st.id)} data-testid={`pccm-config-tab-${st.id}`}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
                style={{
                  background: subTab === st.id ? 'var(--accent-primary)' : 'var(--card-bg-alt)',
                  color: subTab === st.id ? '#fff' : 'var(--text-muted)',
                  border: subTab === st.id ? 'none' : '1px solid var(--border-color)',
                }}>
                <Icon className="w-3 h-3" /> {st.label}
              </button>
            );
          })}
        </div>
        <Button size="sm" variant="outline" onClick={seedAll} disabled={seeding} data-testid="pccm-seed-all-btn">
          {seeding ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Database className="w-3.5 h-3.5 mr-1" />}
          Seed All Defaults
        </Button>
      </div>

      {/* Systems Tab */}
      {subTab === 'systems' && (
        <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border-color)' }}>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: 'var(--card-bg-alt)' }}>
                <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Code</th>
                <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Name</th>
                <th className="p-2 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Color</th>
                <th className="p-2 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Order</th>
                <th className="p-2 w-10" />
              </tr>
            </thead>
            <tbody>
              {systems.map(s => (
                <tr key={s.systemCode} style={{ borderTop: '1px solid var(--border-color)', background: 'var(--card-bg)' }}>
                  <td className="p-2 font-mono text-xs font-semibold" style={{ color: s.displayColor }}>{s.systemCode}</td>
                  <td className="p-2" style={{ color: 'var(--text-secondary)' }}>{s.systemName}</td>
                  <td className="p-2 text-center"><span className="w-4 h-4 rounded-full inline-block" style={{ background: s.displayColor }} /></td>
                  <td className="p-2 text-center text-xs" style={{ color: 'var(--text-muted)' }}>{s.sequenceOrder}</td>
                  <td className="p-2 text-center">
                    <button onClick={() => deleteSystem(s.systemCode)} className="p-1 rounded hover:bg-red-500/10">
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </button>
                  </td>
                </tr>
              ))}
              {systems.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-sm" style={{ color: 'var(--text-muted)' }}>No systems. Click "Seed All Defaults" to populate.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Subsystems Tab */}
      {subTab === 'subsystems' && (
        <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border-color)' }}>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: 'var(--card-bg-alt)' }}>
                <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)' }}>System</th>
                <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Subsystem Code</th>
                <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Name</th>
              </tr>
            </thead>
            <tbody>
              {subsystems.map(s => (
                <tr key={s.subsystemCode} style={{ borderTop: '1px solid var(--border-color)', background: 'var(--card-bg)' }}>
                  <td className="p-2 text-xs font-mono" style={{ color: 'var(--accent-primary)' }}>{s.systemCode}</td>
                  <td className="p-2 font-mono text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{s.subsystemCode}</td>
                  <td className="p-2" style={{ color: 'var(--text-secondary)' }}>{s.subsystemName}</td>
                </tr>
              ))}
              {subsystems.length === 0 && <tr><td colSpan={3} className="p-6 text-center text-sm" style={{ color: 'var(--text-muted)' }}>No subsystems configured.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Stage Gates Tab */}
      {subTab === 'stagegates' && (
        <PCCMStageGateEditor projectId={projectId} configs={configs} onChanged={fetchAll} />
      )}
    </div>
  );
}

/** Editable commissioning gates: rename, reweight, reorder, add, remove, apply. */
function PCCMStageGateEditor({ projectId, configs, onChanged }) {
  const cfg = configs[0];
  const [gates, setGates] = useState(cfg?.stageGates || []);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => { setGates(cfg?.stageGates || []); }, [cfg]);

  if (!cfg) {
    return (
      <div className="p-8 text-center rounded-lg border" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg)' }}>
        <p className="text-sm" style={{ color: 'var(--text-muted)' }} data-testid="pccm-no-gate-config">
          No stage gate configuration. Click "Seed All Defaults" to create the default commissioning gates.
        </p>
      </div>
    );
  }

  const total = gates.reduce((s, g) => s + (Number(g.weightagePercent) || 0), 0);
  const patch = (i, field, value) => setGates(p => p.map((g, idx) => (idx === i ? { ...g, [field]: value } : g)));
  const move = (i, dir) => setGates(p => {
    const n = [...p];
    const j = i + dir;
    if (j < 0 || j >= n.length) return p;
    [n[i], n[j]] = [n[j], n[i]];
    return n.map((g, idx) => ({ ...g, sequenceOrder: idx + 1 }));
  });
  const addGate = () => setGates(p => [...p, {
    stageGateId: `GATE-${p.length + 1}`, stageGateName: 'New gate', sequenceOrder: p.length + 1,
    weightagePercent: 0, description: '', isMilestone: false, isTerminal: false,
  }]);

  const save = async () => {
    if (Math.abs(total - 100) > 0.01) { toast.error(`Weights must total 100% — currently ${total}%`); return; }
    if (gates.some(g => !g.stageGateId || !g.stageGateName)) { toast.error('Every gate needs an id and a name'); return; }
    setSaving(true);
    try {
      await api.put(`/v1/projects/${projectId}/pccm/stage-gate-configs/${cfg.id}`, {
        stageGates: gates.map((g, idx) => ({
          stageGateId: g.stageGateId, stageGateName: g.stageGateName, sequenceOrder: idx + 1,
          weightagePercent: Number(g.weightagePercent) || 0, description: g.description || '',
          isMilestone: !!g.isMilestone, isTerminal: !!g.isTerminal,
        })),
      });
      toast.success('Stage gates saved');
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save gates'); }
    setSaving(false);
  };

  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post(`/v1/projects/${projectId}/pccm/stage-gate-configs/${cfg.id}/apply`);
      toast.success(`Applied ${data.gates} gates to ${data.applied} register item(s) — captured dates kept`);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to apply'); }
    setApplying(false);
  };

  return (
    <div className="rounded-lg border" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg)' }}
         data-testid="pccm-stage-gate-editor">
      <div className="p-4 flex items-center gap-2" style={{ borderBottom: '1px solid var(--border-color)' }}>
        <div>
          <h4 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{cfg.configName}</h4>
          <p className="text-[10px]" style={{ color: total === 100 ? 'var(--text-muted)' : '#f59e0b' }}
             data-testid="pccm-gate-total">Total weightage: {Math.round(total * 100) / 100}% {total !== 100 && '— must be 100%'}</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={addGate} data-testid="pccm-add-gate-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add gate
          </Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid="pccm-save-gates-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null} Save gates
          </Button>
          <Button size="sm" variant="outline" onClick={apply} disabled={applying} data-testid="pccm-apply-gates-btn">
            {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
            Apply to register
          </Button>
        </div>
      </div>
      <div className="p-4 space-y-2">
        {gates.map((sg, idx) => (
          <div key={`${sg.stageGateId}-${idx}`} className="flex items-center gap-2 p-2 rounded-lg border"
               style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg-alt)' }}
               data-testid={`pccm-gate-row-${idx}`}>
            <div className="flex flex-col">
              <button onClick={() => move(idx, -1)} data-testid={`pccm-gate-up-${idx}`}
                      style={{ color: 'var(--text-muted)' }}>▲</button>
              <button onClick={() => move(idx, 1)} data-testid={`pccm-gate-down-${idx}`}
                      style={{ color: 'var(--text-muted)' }}>▼</button>
            </div>
            <input value={sg.stageGateId} onChange={e => patch(idx, 'stageGateId', e.target.value.toUpperCase())}
                   className="px-2 py-1 text-[11px] rounded border w-28 font-mono" data-testid={`pccm-gate-id-${idx}`}
                   style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            <input value={sg.stageGateName} onChange={e => patch(idx, 'stageGateName', e.target.value)}
                   className="px-2 py-1 text-xs rounded border flex-1" data-testid={`pccm-gate-name-${idx}`}
                   style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            <input type="number" min="0" max="100" value={sg.weightagePercent}
                   onChange={e => patch(idx, 'weightagePercent', e.target.value)}
                   className="px-2 py-1 text-xs rounded border w-20 text-right" data-testid={`pccm-gate-weight-${idx}`}
                   style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>%</span>
            <label className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--text-muted)' }}>
              <input type="checkbox" checked={!!sg.isMilestone} onChange={e => patch(idx, 'isMilestone', e.target.checked)}
                     data-testid={`pccm-gate-milestone-${idx}`} /> Milestone
            </label>
            <label className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--text-muted)' }}>
              <input type="checkbox" checked={!!sg.isTerminal} onChange={e => patch(idx, 'isTerminal', e.target.checked)}
                     data-testid={`pccm-gate-terminal-${idx}`} /> Terminal
            </label>
            <button onClick={() => setGates(p => p.filter((_, i) => i !== idx))} className="p-1 rounded hover:bg-red-500/10"
                    data-testid={`pccm-gate-delete-${idx}`}>
              <Trash2 className="w-3.5 h-3.5 text-red-400" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
